import { api } from '@/services/api';
import { useTenants } from '@/services/tenants/useTenants';
import { getApiError } from '@/types/apiError';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/useToast';
import { GET_ASSET_QUERY_KEY } from './useGetAsset';
import { GET_SUPPLEMENTAL_ASSETS_QUERY_KEY } from './useGetSupplementalAssets';

export const useAddSupplementalAssets = () => {
  const queryClient = useQueryClient();
  const { getTenantId } = useTenants();
  const { showToast } = useToast();

  const { mutate, mutateAsync, isPending, error } = useMutation({
    mutationFn: ({ assetId, supplementalAssetIds }: { assetId: string; supplementalAssetIds: string[] }) =>
      api.assets.updateAsset(getTenantId(), assetId, { supplementalAssetIds }),
    onSuccess: (_data, { assetId, supplementalAssetIds }) => {
      queryClient.resetQueries({ queryKey: [GET_ASSET_QUERY_KEY, { assetId }] });
      queryClient.resetQueries({ queryKey: [GET_SUPPLEMENTAL_ASSETS_QUERY_KEY], exact: false });

      const count = supplementalAssetIds.length;
      showToast({
        message:
          count === 1 ? 'Supplemental asset added successfully' : `${count} supplemental assets added successfully`,
        type: 'success',
      });
    },
    onError: _error => {
      showToast({
        message: 'Failed to add supplemental assets',
        type: 'error',
      });
    },
  });

  return {
    addSupplementalAssets: mutate,
    addSupplementalAssetsAsync: mutateAsync,
    isAdding: isPending,
    addError: getApiError(error),
  };
};
